import React from 'react';
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Signin from "./Signin";
import Overlay from "./Overlay";
import { useSigninModalOptional } from "../../context/SigninModalContext";
import HomeLogo from "../../components/Logo/home";
import CartLogo from "../../components/Logo/cart";
import ShareLogo from "../../components/Logo/share";
import BagLogo from "../../components/Logo/bag";
import UserLogo from "../../components/Logo/user";
import SocialLogo from "../../components/Logo/social";
import AssistantLogo from "../../components/Logo/assistant";
import EarningsLogo from "../../components/Logo/earnings";
import { Add } from "@material-ui/icons";
import api from "../../api";
import NotificationBell from "../NotificationBell";
import useTabScrub, { useTabBarOffset } from "./useTabScrub";
import "../../views/layout.scss";
import "../../styles/_header.scss";
import "../../styles/_global.scss";

const Header = () => {
  // const pathname = usePathname(); // Using useNavigation hook
  // const router = useRouter();


  const [header, setHeader] = useState(false);

  const signinModal = useSigninModalOptional();
  const [localModalOpen, setLocalModalOpen] = useState(false);

  const openModal = () => {
    if (signinModal) {
      signinModal.openSignin();
    } else {
      document.body.classList.add("no-scroll");
      setLocalModalOpen(true);
    }
  };

  const closeModal = () => {
    if (signinModal) {
      signinModal.closeSignin();
    } else {
      document.body.classList.remove("no-scroll");
      setLocalModalOpen(false);
    }
  };

  const modalOpen = signinModal ? signinModal.isOpen : localModalOpen;

  const history = useHistory();
  const { barRef: tabBarRef, lens: tabLens } = useTabScrub();
  useTabBarOffset();

  const location = useLocation();

  const [page, setPage] = useState("");
  const [cartCount, setCartCount] = useState(0);
  const isMountedRef = useRef(true);

  const { email, _id } = useSelector((state) => state.auth);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!email) return;
    api
      .getPurchaseCartCount()
      .then((res) => {
        if (isMountedRef.current) setCartCount(res.count || 0);
      })
      .catch(() => {
        if (isMountedRef.current) setCartCount(0);
      });
  }, [email, location.pathname]);

  // Phone tab bar: short labels under each icon that show when the bar
  // first appears and whenever it's touched, then fade down and away.
  const [showTabLabels, setShowTabLabels] = useState(true);
  const tabLabelTimer = useRef(null);
  const hideTabLabelsIn = useCallback((ms) => {
    clearTimeout(tabLabelTimer.current);
    tabLabelTimer.current = setTimeout(() => setShowTabLabels(false), ms);
  }, []);

  useEffect(() => {
    if (!email) return undefined;
    setShowTabLabels(true);
    hideTabLabelsIn(2600);
    const bar = tabBarRef.current;
    if (!bar) return undefined;
    const onTouch = () => {
      clearTimeout(tabLabelTimer.current);
      setShowTabLabels(true);
    };
    const onRelease = () => hideTabLabelsIn(1400);
    bar.addEventListener("touchstart", onTouch, { passive: true });
    bar.addEventListener("touchend", onRelease, { passive: true });
    bar.addEventListener("touchcancel", onRelease, { passive: true });
    return () => {
      clearTimeout(tabLabelTimer.current);
      bar.removeEventListener("touchstart", onTouch);
      bar.removeEventListener("touchend", onRelease);
      bar.removeEventListener("touchcancel", onRelease);
    };
  }, [email, tabBarRef, hideTabLabelsIn]);

  const goTo = useCallback(
    (url) => {
      history.push(url);
    },
    [history]
  );

  useEffect(() => {
    if (location.pathname.indexOf("/privacy-policy") === 0) setPage("privacy");
    else if (location.pathname.indexOf("/terms-of-service") === 0)
      setPage("terms");
    else if (location.pathname.indexOf("/personal-data") === 0)
      setPage("personal");
    else if (location.pathname.indexOf("/support") === 0) setPage("support");
    else if (location.pathname.indexOf("/signin") === 0) setPage("signin");
    else if (location.pathname.indexOf("/profile") === 0) setPage("profile");
    else if (location.pathname.indexOf("/share/together") === 0) {
      document.title = "My OllaCart";
      setPage("singleshare");
    } else if (location.pathname.indexOf("/share") === 0) setPage("share");
    else if (location.pathname.indexOf("/order") === 0) setPage("order");
    else if (location.pathname.indexOf("/purchase") === 0) setPage("purchase");
    else if (location.pathname.indexOf("/affiliate") === 0) setPage("affiliate");
    else if (location.pathname.indexOf("/earnings") === 0) setPage("affiliate");
    else if (location.pathname.indexOf("/social") === 0) setPage("social");
    else if (location.pathname.indexOf("/home") === 0) setPage("home");
    else if (location.pathname.indexOf("/assistant") === 0) setPage("assistant");
    else if (location.pathname.indexOf("/blog") === 0) setPage("blog");
    else setPage("landing");
  }, [email, location, goTo]);

  const setMenuOpen = useCallback((open) => {
    setHeader(open);
    if (!signinModal || !signinModal.isOpen) {
      document.body.classList.toggle("no-scroll", open);
    }
  }, [signinModal]);

  useEffect(() => {
    if (!signinModal) return undefined;
    return signinModal.registerMenuClose(() => setMenuOpen(false));
  }, [signinModal, setMenuOpen]);

  function ToggleHeader() {
    setMenuOpen(!header);
  }

  // Never leave the homepage locked if the menu unmounts or route changes
  useEffect(() => {
    return () => {
      if (!signinModal || !signinModal.isOpen) {
        document.body.classList.remove("no-scroll");
      }
    };
  }, [signinModal]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, setMenuOpen]);
  return (
    <>
      {/* Portals outside the fixed header so they never inherit a full-screen hit target */}
      {!email && !signinModal && (
        <>
          <Signin isOpen={modalOpen} closeModal={closeModal} />
          <Overlay isOpen={modalOpen} closeModal={closeModal} />
        </>
      )}

{ !email? (
      <div id="header-main" className={header ? "header-main--open" : "header-main--closed"}>
          <div
          className="header-container"
          style={{
            height: header ? "100dvh" : "72px",
            background: header ? "inherit" : "none",
          }}
        >
          <div className="header-logo">
            <Link
              to="/"
              onClick={() => {
                if (header) {
                  ToggleHeader();
                }
              }}
            >
              <svg width="140" height="63" viewBox="0 0 140 63" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M60.9211 39.3033C60.9211 42.5692 58.4372 44.9608 55.1025 44.9608C51.7687 44.9608 49.2852 42.5688 49.2852 39.3033C49.2852 36.0381 51.7687 33.6465 55.1025 33.6465C58.4375 33.6465 60.9211 36.0384 60.9211 39.3033ZM51.676 39.3033C51.676 41.3961 53.0791 42.7531 55.1022 42.7531C57.1264 42.7531 58.5287 41.3961 58.5287 39.3033C58.5287 37.2111 57.1264 35.8542 55.1022 35.8542C53.0791 35.8542 51.676 37.2111 51.676 39.3033Z" fill="white" />
                <path d="M65.3593 44.616H63.1055V28.5186H65.3593V44.616Z" fill="white" />
                <path d="M70.6011 44.616H68.3477V28.5186H70.6011V44.616Z" fill="white" />
                <path d="M72.7852 39.3033C72.7852 35.7626 75.1538 33.6465 77.9593 33.6465C79.707 33.6465 80.8797 34.4057 81.5465 35.5555H81.5923V33.9914H83.8458V44.6159H81.5923V43.0518H81.5465C80.8797 44.2016 79.707 44.9608 77.9593 44.9608C75.1538 44.9612 72.7852 42.8458 72.7852 39.3033ZM81.7079 39.3033C81.7079 37.2802 80.4204 35.8542 78.4419 35.8542C76.4643 35.8542 75.1767 37.2802 75.1767 39.3033C75.1767 41.3271 76.4643 42.7531 78.4419 42.7531C80.4204 42.7531 81.7079 41.3271 81.7079 39.3033Z" fill="white" />
                <path d="M86.4922 39.3033C86.4922 35.9236 88.9067 33.6465 92.3336 33.6465C93.7127 33.6465 94.8854 34.0146 95.9666 34.7276L94.9087 36.6359C94.1732 36.1762 93.253 35.8313 92.2642 35.8313C90.2171 35.8313 88.8838 37.3027 88.8838 39.3036C88.8838 41.3046 90.2171 42.7767 92.2642 42.7767C93.253 42.7767 94.1732 42.4319 94.9087 41.9714L95.9666 43.8804C94.8854 44.5934 93.7127 44.9616 92.3336 44.9616C88.9067 44.9612 86.4922 42.684 86.4922 39.3033Z" fill="white" />
                <path d="M97.1172 39.3033C97.1172 35.7626 99.4863 33.6465 102.291 33.6465C104.039 33.6465 105.211 34.4057 105.879 35.5555H105.924V33.9914H108.177V44.6159H105.924V43.0518H105.879C105.211 44.2016 104.039 44.9608 102.291 44.9608C99.4863 44.9612 97.1172 42.8458 97.1172 39.3033ZM106.04 39.3033C106.04 37.2802 104.752 35.8542 102.774 35.8542C100.797 35.8542 99.5084 37.2802 99.5084 39.3033C99.5084 41.3271 100.797 42.7531 102.774 42.7531C104.752 42.7531 106.04 41.3271 106.04 39.3033Z" fill="white" />
                <path d="M116.916 36.315H116.571C114.364 36.315 113.19 37.3729 113.19 39.9026V44.6167H110.938V33.9921H113.19V36.3146H113.237C113.743 34.7284 114.961 33.6709 116.755 33.6709H116.916V36.315H116.916Z" fill="white" />
                <path d="M121.628 35.9007V41.4659C121.628 42.2243 121.95 42.5696 122.732 42.5696H123.284V44.6395H122.249C120.386 44.6395 119.374 43.6507 119.374 41.9027V35.9007H117.488V33.9917H119.374V29.7373H121.628V33.9917H123.789V35.9007H121.628Z" fill="white" />
                <path d="M33.9183 40.2165C33.5643 41.7761 32.3027 42.7447 30.5955 42.7447C28.5717 42.7447 27.1689 41.3877 27.1689 39.2948C27.1689 38.0947 27.6324 37.1386 28.408 36.5393L27.6053 34.6021L27.888 35.2831L27.5306 34.4209C25.8493 35.3815 24.7773 37.1493 24.7773 39.2945C24.7773 42.5604 27.2609 44.952 30.5955 44.952C33.6074 44.952 35.9242 43.0007 36.3442 40.2162H36.0394L33.9183 40.2165Z" fill="#009FB7" />
                <path d="M57.0194 31.8649C56.8576 31.8649 56.6928 31.8298 56.5376 31.7554C55.9779 31.4891 55.7402 30.8192 56.0065 30.2592C56.8233 28.5447 57.5107 26.6704 57.6683 25.7873C55.8505 25.5714 50.0259 25.7057 44.8246 25.9906C44.2077 26.0242 43.6763 25.5504 43.6423 24.9312C43.6084 24.3121 44.083 23.7829 44.7017 23.749C44.7334 23.7471 47.9341 23.5723 51.2333 23.4861C58.207 23.3026 58.8063 23.6696 59.2027 23.9123C60.3376 24.6077 60.0305 26.2127 59.2206 28.4081C58.6934 29.8365 58.0601 31.169 58.033 31.2251C57.8411 31.6291 57.439 31.8649 57.0194 31.8649Z" fill="#FFC827" />
                <path d="M46.0099 37.9692H31.9573C31.665 37.9692 31.361 37.812 31.2812 37.6197C31.2809 37.6182 31.2801 37.6175 31.2797 37.6159L24.826 22.0739L24.8256 22.0743L24.3347 20.8917C24.1714 20.4984 23.6895 20.1768 23.2634 20.1768H22.6526H21.6989H15.3841C14.7641 20.1768 14.2617 20.6792 14.2617 21.2991C14.2617 21.919 14.7641 22.4215 15.3841 22.4215H21.6989H21.7649C22.1888 22.4215 22.6679 22.7404 22.8335 23.1314L29.0556 38.1115C29.0579 38.1168 29.0591 38.121 29.061 38.1271L29.5714 39.3559C29.7671 39.8274 30.3454 40.2135 30.8566 40.2135H31.559C31.5613 40.2135 31.5632 40.2135 31.5651 40.2135H46.0099C46.6302 40.2135 47.1327 39.7107 47.1327 39.0908C47.1327 38.4724 46.6302 37.9692 46.0099 37.9692Z" fill="#FE4A49" />
              </svg>

            </Link>
            <button
              type="button"
              className="modal-icon"
              onClick={() => {
                setMenuOpen(false);
                openModal();
              }}
              aria-label="Sign in"
            >
              <svg width="51" height="50" viewBox="0 0 51 50" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M28.9506 38.8361H23.3992C22.799 38.8361 22.3125 38.3496 22.3125 37.7496C22.3125 37.1496 22.799 36.6631 23.3992 36.6631H28.9506C29.5504 36.6631 30.0369 37.1496 30.0369 37.7496C30.0369 38.3496 29.5504 38.8361 28.9506 38.8361Z" fill="#FFC827" />
                <path d="M31.4629 16.882C31.4629 20.0432 29.0584 22.358 25.8311 22.358C22.6037 22.358 20.1992 20.0432 20.1992 16.882C20.1992 13.7215 22.6037 11.4062 25.8311 11.4062C29.0584 11.4062 31.4629 13.7213 31.4629 16.882ZM22.5143 16.882C22.5143 18.908 23.8721 20.2215 25.8311 20.2215C27.79 20.2215 29.1473 18.908 29.1473 16.882C29.1473 14.8566 27.79 13.5432 25.8311 13.5432C23.8721 13.5432 22.5143 14.8564 22.5143 16.882Z" fill="#FE4A49" />
                <path d="M33.8153 36.8131C33.4368 36.8131 33.0689 36.6148 32.8689 36.2619C32.5732 35.7396 32.7566 35.0765 33.279 34.7808C34.565 34.0525 36.039 32.8158 36.3204 31.5289C36.7737 29.455 36.3425 27.8193 34.963 26.3816C33.8603 25.2326 33.0972 24.7474 31.3634 23.8736C30.8276 23.6033 30.6118 22.95 30.8823 22.414C31.1521 21.8781 31.8058 21.6629 32.3417 21.933C34.2054 22.8724 35.1995 23.4892 36.5312 24.8769C38.4054 26.83 39.0487 29.2242 38.4437 31.9929C37.9116 34.4244 35.4038 36.075 34.3501 36.6717C34.1808 36.7675 33.997 36.8131 33.8153 36.8131Z" fill="#FFC827" />
                <path d="M17.8447 36.813C17.6629 36.813 17.4793 36.7675 17.3099 36.6716C16.256 36.0749 13.7482 34.4244 13.2164 31.9929C12.6111 29.2242 13.2547 26.83 15.1289 24.8769C16.4603 23.4892 17.4545 22.8724 19.3183 21.9329C19.8545 21.6628 20.5078 21.8783 20.7775 22.414C21.048 22.9499 20.8324 23.6031 20.2966 23.8736C18.5629 24.7474 17.7996 25.2326 16.6968 26.3816C15.3175 27.8191 14.8861 29.4548 15.3392 31.5288C15.6205 32.8158 17.0949 34.0525 18.381 34.7806C18.9031 35.0763 19.0865 35.7394 18.7912 36.2617C18.5908 36.615 18.2232 36.813 17.8447 36.813Z" fill="#009FB7" />
              </svg>
            </button>
            <button
              type="button"
              className="hamburger-icon"
              onClick={ToggleHeader}
              aria-label={header ? "Close menu" : "Open menu"}
              aria-expanded={header}
            >
              <svg width="51" height="50" viewBox="0 0 51 50" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M14 17H37" stroke="#FFC827" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M14 25H37" stroke="#FE4A49" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M14 33H37" stroke="#009FB7" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="header-navigation">

            <div className="navigation-wrap">
              <Link to="/about" onClick={() => setMenuOpen(false)}>
                About us
              </Link>
              <Link to="/blog" onClick={() => setMenuOpen(false)}>
                Blog
              </Link>

              <div className="btn-container">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    openModal();
                  }}
                >
                  <svg width="41" height="41" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M23.3417 31.8097H18.9006C18.4205 31.8097 18.0312 31.4205 18.0312 30.9405C18.0312 30.4605 18.4205 30.0713 18.9006 30.0713H23.3417C23.8216 30.0713 24.2108 30.4605 24.2108 30.9405C24.2108 31.4205 23.8216 31.8097 23.3417 31.8097Z" fill="#2B2D42" />
                    <path d="M25.3547 14.2468C25.3547 16.7757 23.4311 18.6276 20.8492 18.6276C18.2673 18.6276 16.3438 16.7757 16.3438 14.2468C16.3438 11.7184 18.2673 9.86621 20.8492 9.86621C23.4311 9.86621 25.3547 11.7182 25.3547 14.2468ZM18.1958 14.2468C18.1958 15.8676 19.282 16.9184 20.8492 16.9184C22.4164 16.9184 23.5022 15.8676 23.5022 14.2468C23.5022 12.6265 22.4164 11.5757 20.8492 11.5757C19.282 11.5757 18.1958 12.6264 18.1958 14.2468Z" fill="#FE4A49" />
                    <path d="M27.2366 30.1917C26.9338 30.1917 26.6395 30.0331 26.4795 29.7507C26.2429 29.3329 26.3896 28.8024 26.8076 28.5659C27.8363 27.9832 29.0156 26.9938 29.2407 25.9643C29.6034 24.3053 29.2584 22.9967 28.1548 21.8465C27.2726 20.9273 26.6621 20.5392 25.2751 19.8401C24.8465 19.6238 24.6738 19.1012 24.8902 18.6724C25.106 18.2437 25.629 18.0715 26.0577 18.2876C27.5487 19.0392 28.344 19.5326 29.4093 20.6428C30.9087 22.2053 31.4234 24.1206 30.9393 26.3356C30.5137 28.2807 28.5074 29.6012 27.6645 30.0785C27.529 30.1553 27.382 30.1917 27.2366 30.1917Z" fill="#FFC827" />
                    <path d="M14.4609 30.1916C14.3154 30.1916 14.1686 30.1552 14.0331 30.0785C13.19 29.6012 11.1837 28.2807 10.7583 26.3355C10.274 24.1205 10.7889 22.2052 12.2883 20.6427C13.3534 19.5326 14.1487 19.0391 15.6398 18.2876C16.0687 18.0715 16.5914 18.2438 16.8072 18.6724C17.0236 19.1012 16.8511 19.6237 16.4225 19.8401C15.0354 20.5391 14.4248 20.9273 13.5426 21.8465C12.4392 22.9965 12.094 24.3051 12.4565 25.9643C12.6815 26.9938 13.8611 27.9832 14.89 28.5657C15.3076 28.8023 15.4543 29.3327 15.2181 29.7505C15.0578 30.0332 14.7637 30.1916 14.4609 30.1916Z" fill="#009FB7" />
                  </svg>
                  <p>Sign in</p>
                </button>
                <a href="https://chromewebstore.google.com/detail/ollacart-extension/hpbmlmabfkbhmhjhocddfckebbnbkcbm" target="_blank" rel="noopener noreferrer">

                  <div className="btn">
                    <svg width="41" height="41" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M36.7691 25.3675C36.7691 27.593 35.0766 29.2225 32.8042 29.2225C30.5322 29.2225 28.8398 27.593 28.8398 25.3675C28.8398 23.1427 30.5322 21.5127 32.8042 21.5127C35.0766 21.5129 36.7691 23.1427 36.7691 25.3675ZM30.4692 25.3675C30.4692 26.7936 31.4253 27.7183 32.8041 27.7183C34.183 27.7183 35.1389 26.7936 35.1389 25.3675C35.1389 23.9418 34.183 23.0172 32.8041 23.0172C31.4253 23.0172 30.4692 23.9418 30.4692 25.3675Z" fill="#2B2D42" />
                      <path d="M18.3661 25.9894C18.1252 27.0519 17.2655 27.7121 16.1017 27.7121C14.7228 27.7121 13.7669 26.7874 13.7669 25.3613C13.7669 24.5433 14.0827 23.8916 14.6111 23.4832L14.0641 22.163L14.2564 22.6272L14.013 22.04C12.8675 22.6946 12.1367 23.8994 12.1367 25.3613C12.1367 27.5866 13.8292 29.2163 16.1016 29.2163C18.1541 29.2163 19.7328 27.8864 20.0189 25.9894H19.8111H18.3661Z" fill="#009FB7" />
                      <path d="M34.1088 20.299C33.9987 20.299 33.8865 20.2751 33.7804 20.2244C33.3991 20.0427 33.2372 19.5865 33.4188 19.2049C33.9754 18.0366 34.4438 16.7594 34.551 16.1577C33.3124 16.0104 29.3432 16.1019 25.799 16.2962C25.3785 16.3191 25.0165 15.9962 24.9933 15.5743C24.9704 15.1524 25.2937 14.7918 25.7154 14.7687C25.7371 14.7674 27.9179 14.6483 30.166 14.5894C34.9182 14.4644 35.3265 14.7146 35.5966 14.8801C36.3699 15.354 36.1608 16.4476 35.6087 17.9438C35.2496 18.9171 34.8179 19.8251 34.7997 19.8632C34.6687 20.1382 34.3946 20.299 34.1088 20.299Z" fill="white" />
                      <path d="M26.6073 24.4585H17.0313C16.8322 24.4585 16.6247 24.351 16.5703 24.2202C16.5702 24.2193 16.5695 24.2185 16.5691 24.2177L12.1717 13.6268L12.1711 13.6271L11.8364 12.8215C11.7253 12.5534 11.397 12.334 11.1066 12.334H10.6906H10.0408H5.7375C5.31516 12.334 4.97266 12.6765 4.97266 13.099C4.97266 13.5215 5.315 13.8638 5.7375 13.8638H10.0408H10.0856C10.3744 13.8638 10.7011 14.0813 10.8138 14.3479L15.0539 24.5559C15.0553 24.5593 15.0562 24.5626 15.0575 24.5662L15.4055 25.4035C15.5388 25.7252 15.933 25.988 16.2812 25.988H16.7598C16.7613 25.988 16.7627 25.9879 16.7638 25.9879L26.607 25.988C27.0294 25.988 27.372 25.6457 27.372 25.223C27.3722 24.8009 27.0297 24.4585 26.6073 24.4585Z" fill="#FE4A49" />
                    </svg>
                    <p>Download</p>
                  </div>
                </a>

              </div>
            </div>

          </div>

          {/* <div className="header-link">
              <p>WEBMETRIC HAS A VISION </p>
  </div> */}
        </div>
       </div>
  ) : (
    <div className={`menu${tabLens ? " is-scrubbing" : ""}${showTabLabels ? " show-labels" : ""}`} ref={tabBarRef}>
      {tabLens && (
        <span
          className={`menu-lens${tabLens.active ? " is-over-item" : ""}`}
          style={{ transform: `translateX(${tabLens.x}px)` }}
          aria-hidden="true"
        />
      )}
      <div className="d-flex justify-content-between">
        <div className="right-menu">
          {email ? (
            <>
              <div
                className={"menu-item" + (page === "home" ? " active" : "")}
                data-tab="home"
              >
                <HomeLogo onClick={() => goTo("/home")} />
                <span className="menu-label" aria-hidden="true">home</span>
              </div>
              <div
                className={
                  "menu-item" + (page === "purchase" ? " active" : "")
                }
                style={{ position: "relative" }}
                data-tab="buy"
              >
                <CartLogo onClick={() => goTo(`/purchase`)} />
                <span className="menu-label" aria-hidden="true">buy</span>
                {cartCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-4px",
                      right: "-4px",
                      minWidth: "18px",
                      height: "18px",
                      borderRadius: "9px",
                      backgroundColor: "#EC1C24",
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 4px",
                    }}
                  >
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </div>
              {/* Phones: Shopping Assistant as a centered (+) between Cart
                  and Share; notifications, earnings, orders and account
                  move above the footer (MobileQuickLinks). */}
              <div
                className={"menu-item menu-item--plus" + (page === "assistant" ? " active" : "")}
                title="Shopping Assistant"
                data-tab="assistant"
              >
                <Add onClick={() => goTo("/assistant")} aria-label="Shopping Assistant" />
                <span className="menu-label" aria-hidden="true">assistant</span>
              </div>
              <div
                className={"menu-item" + (page === "share" ? " active" : "")}
                data-tab="share"
              >
                <ShareLogo onClick={() => goTo(`/share/${_id}`)} />
                <span className="menu-label" aria-hidden="true">share</span>
              </div>
              <div
                className={"menu-item" + (page === "social" ? " active" : "")}
                data-tab="social"
              >
                <SocialLogo onClick={() => goTo(`/social`)} />
                <span className="menu-label" aria-hidden="true">social</span>
              </div>
              <div
                className={"menu-item menu-item--desktop" + (location.pathname.startsWith("/assistant") ? " active" : "")}
                title="Shopping Assistant"
              >
                <AssistantLogo onClick={() => goTo("/assistant")} />
              </div>
            </>
          ) : (
            <>
              <div
                className={
                  "menu-item" + (page === "landing" ? " active" : "")
                }
              >
                <HomeLogo onClick={() => goTo("/")} />
              </div>
            </>
          )}
        </div>
        <div className="right-menu">
          {email && (
            <div className="menu-item menu-item--desktop" title="Notifications">
              <NotificationBell />
            </div>
          )}
          {email && (
            <div
              className={"menu-item menu-item--desktop" + (page === "affiliate" ? " active" : "")}
              title="Earnings"
            >
              <EarningsLogo onClick={() => goTo("/affiliate")} />
            </div>
          )}
          {email && (
            <div
              className={"menu-item menu-item--desktop" + (page === "order" ? " active" : "")}
            >
              <BagLogo onClick={() => goTo(`/order`)} />
            </div>
          )}
          <div
            className={"menu-item menu-item--desktop" + (page === "profile" ? " active" : "")}
          >
            {email && <UserLogo onClick={() => goTo("/profile")} />}
            {!email && (
              <>
                <UserLogo onClick={openModal} />
                {/* {isIPhone && <SigninArrow />} */}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    )}
    </>
  );
};

export default Header;
